import { useEffect, useMemo, useState } from 'react';
import ModalShell from './ModalShell.jsx';

// Round the current moment up to the next quarter-hour for a sensible default
// (15 / 30 / 45 / 00). Avoids defaulting to "now + 5 min" which then gets
// rejected by the server-side future-time check while the user is still typing.
function defaultScheduleLocal() {
  const d = new Date();
  d.setSeconds(0, 0);
  const minutes = d.getMinutes();
  const rounded = Math.ceil((minutes + 1) / 15) * 15;
  d.setMinutes(rounded);
  // datetime-local wants "YYYY-MM-DDTHH:mm" in the user's local zone.
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function nowLocalString() {
  const d = new Date();
  d.setSeconds(0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function formatPretty(localDatetime) {
  if (!localDatetime) return null;
  const d = new Date(localDatetime);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function ScheduleSendModal({ isoDate, onCancel, onConfirm }) {
  const [scheduledFor, setScheduledFor] = useState(defaultScheduleLocal);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Set when the server refuses (409) because the edition was already pushed to
  // Beehiiv. Rather than hard-failing, we ask the editor to confirm and then
  // retry the schedule with force=true.
  const [alreadySent, setAlreadySent] = useState(false);

  const minLocal = useMemo(() => nowLocalString(), []);

  // Re-validate as the user types so the disabled state matches the future-time
  // requirement on the server.
  const parsed = scheduledFor ? new Date(scheduledFor) : null;
  const inFuture = parsed && !Number.isNaN(parsed.getTime()) && parsed.getTime() > Date.now();
  const pretty = formatPretty(scheduledFor);

  useEffect(() => {
    if (error) setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledFor]);

  async function handleConfirm(force = false) {
    if (!inFuture) {
      setError('Pick a date and time in the future.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Convert local datetime to UTC ISO so the server stores the actual moment.
      await onConfirm(new Date(scheduledFor).toISOString(), { force });
    } catch (err) {
      // 409 = edition already pushed/sent to Beehiiv. Don't hard-fail — surface a
      // confirmation so the editor can choose to schedule anyway (force=true).
      if (err?.status === 409) {
        setAlreadySent(true);
        setSubmitting(false);
        return;
      }
      const msg = err?.data?.error || err?.message || 'Schedule failed.';
      const retryHint = err?.data?.retryAfterMs
        ? ` Retry in ~${Math.ceil(err.data.retryAfterMs / 1000)}s.`
        : '';
      setError(msg + retryHint);
      setSubmitting(false); // keep modal open so the editor can adjust + retry
    }
  }

  return (
    <ModalShell
      eyebrow={`Brief · ${isoDate}`}
      title="Schedule send"
      onClose={submitting ? undefined : onCancel}
      widthClass="max-w-md"
    >
      <div className="space-y-4">
        <p className="text-[13px] text-hxd-muted leading-relaxed m-0">
          Hands the rendered brief to Beehiiv with the send time you pick. Beehiiv handles the
          actual dispatch — you can still adjust or cancel inside the Beehiiv app afterward.
        </p>

        <div>
          <label
            htmlFor="schedule-datetime"
            className="block font-mono text-[10px] font-bold tracking-[0.16em] uppercase text-[#737373] mb-1.5"
          >
            Send time (your local timezone)
          </label>
          <input
            id="schedule-datetime"
            type="datetime-local"
            value={scheduledFor}
            min={minLocal}
            onChange={(e) => setScheduledFor(e.target.value)}
            disabled={submitting}
            className="w-full bg-white border border-[#D4D4D4] px-3 py-2 text-[14px] text-[#0A0A0A] focus:outline-none focus:border-black transition-colors disabled:opacity-50 cursor-pointer"
          />
          {pretty && inFuture && (
            <p className="text-[12px] text-hxd-muted mt-1.5 m-0">
              Subscribers receive the email <span className="font-semibold text-hxd-ink">{pretty}</span>.
            </p>
          )}
          {pretty && !inFuture && (
            <p className="text-[12px] text-[#c0392b] mt-1.5 m-0">
              Must be in the future.
            </p>
          )}
        </div>

        {error && (
          <div
            role="alert"
            className="text-[12px] text-[#c0392b] bg-[#fdecea] border border-[#f5c6cb] rounded px-3 py-2"
          >
            {error}
          </div>
        )}

        {alreadySent && (
          <div
            role="alert"
            className="text-[12px] text-[#8a5a00] bg-[#fff4e0] border border-[#f0d28a] rounded px-3 py-2"
          >
            This edition has already been pushed to Beehiiv (it may show as sent).
            Scheduling again overwrites the existing Beehiiv post. Schedule anyway?
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-hxd-border-soft">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="hxd-btn hxd-btn-ghost hxd-btn-sm"
          >
            Cancel
          </button>
          {alreadySent ? (
            <button
              type="button"
              onClick={() => handleConfirm(true)}
              disabled={submitting || !inFuture}
              className="hxd-btn hxd-btn-primary hxd-btn-sm"
            >
              {submitting ? 'Scheduling…' : 'Schedule anyway →'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleConfirm(false)}
              disabled={submitting || !inFuture}
              className="hxd-btn hxd-btn-primary hxd-btn-sm"
            >
              {submitting ? 'Scheduling…' : 'Schedule send →'}
            </button>
          )}
        </div>
      </div>
    </ModalShell>
  );
}
